import { useEffect, useState } from "react";
import {
  WEEKDAY_LABELS,
  holidaysForYear,
  type SiteSettings,
  type Weekday,
} from "@shared/modules/x-site";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { HoursEditor } from "../../../core/components/ui/hours-editor";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Textarea } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Toggle } from "../../../core/components/ui/toggle";
import { useToastStore } from "../../../core/stores/toast";
import { AutomaticBookingCard } from "../components/automatic-booking";
import { getSettings, updateSettings, type SiteSettingsResponse } from "../lib/api";

/**
 * Réglages du site public : réservation en ligne (heures, pas, délai,
 * horizon, acompte), coordonnées affichées, état de Stripe, lien vers le site.
 */
const WEEKDAYS: Weekday[] = ["1", "2", "3", "4", "5", "6", "7"];
const holidayDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

export function SitePage() {
  const push = useToastStore((s) => s.push);
  const [data, setData] = useState<SiteSettingsResponse | null>(null);
  const [draft, setDraft] = useState<SiteSettings | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSettings()
      .then((d) => {
        setData(d);
        setDraft(d.settings);
      })
      .catch(() => push("danger", "Impossible de charger les réglages du site"));
  }, [push]);

  const set = <K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  };

  const save = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      const saved = await updateSettings(draft);
      setData(saved);
      setDraft(saved.settings);
      setDirty(false);
      push("success", "Site mis à jour", "Les prochaines visites voient ces réglages.");
    } catch {
      push(
        "danger",
        "Enregistrement impossible",
        "Vérifiez les heures (HH:MM), le courriel et les adresses.",
      );
    } finally {
      setBusy(false);
    }
  };

  const saveButton = (
    <Button variant="primary" disabled={busy || !draft || !dirty} onClick={() => void save()}>
      {dirty ? "Enregistrer" : "Enregistré"}
    </Button>
  );

  if (!draft || !data) {
    return (
      <div className="page">
        <PageHeader title="Site web" />
        <p className="empty">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="page site-admin">
      <PageHeader
        title="Site web"
        subtitle="Ce que le site public affiche et propose : heures, réservation en ligne, acompte, coordonnées."
        actions={
          <span className="site-admin__actions">
            <a className="btn btn--ghost" href={data.siteOrigin} target="_blank" rel="noreferrer">
              <span className="btn__icon">
                <Icon name="external" size={16} />
              </span>
              Ouvrir le site
            </a>
            {saveButton}
          </span>
        }
      />

      {data.readiness && (
        <Card title="Préparation des réservations">
          <p>
            {data.readiness.bookableServices} tarifs réservables ·{" "}
            {data.readiness.techniciansWithHours} employés avec horaires ·{" "}
            {data.readiness.missingDurations} durées à compléter.
          </p>
          {data.readiness.techniciansWithoutHours > 0 && draft.schedulingMode !== "workshop" && (
            <p>
              Renseignez les horaires de {data.readiness.techniciansWithoutHours} employés dans
              Équipe. Les présences inconnues ne proposent aucun créneau.
            </p>
          )}
          {data.readiness.imported && !draft.onlineBooking && (
            <p>
              La boutique Urable était fermée. Vérifiez les horaires, la capacité et les durées
              avant d’activer la réservation.
            </p>
          )}
        </Card>
      )}
      <div className="site-admin__grid">
        <Card
          title="Réservation en ligne"
          subtitle="Les créneaux respectent les horaires, les rendez-vous existants et le mode de planification choisi."
        >
          <div className="site-admin__stack">
            <Toggle
              checked={draft.onlineBooking}
              onChange={(v) => set("onlineBooking", v)}
              label={
                draft.onlineBooking ? "Réservation en ligne ouverte" : "Réservation en ligne fermée"
              }
              hint={
                draft.onlineBooking
                  ? "Le site propose des créneaux et crée les rendez-vous dans l'agenda."
                  : "Le site renvoie au téléphone."
              }
            />
            <div className="form-grid">
              <Field
                label="Planification de l’agenda"
                hint="La capacité globale permet de réserver avant d’attribuer le rendez-vous à un employé."
              >
                <select
                  className="input"
                  value={draft.schedulingMode ?? "technicians"}
                  onChange={(e) => {
                    set("schedulingMode", e.target.value as "technicians" | "workshop");
                    if (e.target.value === "workshop" && !draft.maxConcurrentAppointments)
                      set("maxConcurrentAppointments", 1);
                  }}
                >
                  <option value="technicians">Selon les présences des employés</option>
                  <option value="workshop">Selon la capacité globale de l’atelier</option>
                </select>
              </Field>
              <Field label="Pas des créneaux (minutes)">
                <Input
                  type="number"
                  min={15}
                  max={120}
                  step={15}
                  value={draft.slotStepMin}
                  onChange={(e) => set("slotStepMin", Number(e.target.value))}
                />
              </Field>
              <Field label="Délai minimal (heures)" hint="Pas de créneau plus tôt que ce délai.">
                <Input
                  type="number"
                  min={0}
                  max={240}
                  value={draft.leadTimeHours}
                  onChange={(e) => set("leadTimeHours", Number(e.target.value))}
                />
              </Field>
              <Field
                label="Travaux simultanés à l’atelier"
                hint={
                  draft.schedulingMode === "workshop"
                    ? "Obligatoire : nombre maximal de rendez-vous simultanés, attribués aux employés ensuite."
                    : "Laisser vide pour limiter seulement par les employés disponibles."
                }
              >
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={draft.maxConcurrentAppointments ?? ""}
                  onChange={(e) =>
                    set("maxConcurrentAppointments", e.target.value ? Number(e.target.value) : null)
                  }
                />
              </Field>
              <Field label="Horizon (jours)">
                <Input
                  type="number"
                  min={7}
                  max={365}
                  value={draft.maxDaysAhead}
                  onChange={(e) => set("maxDaysAhead", Number(e.target.value))}
                />
              </Field>
            </div>
          </div>
        </Card>

        <Card
          title="Pause de midi et fermetures"
          subtitle="Indisponibilités internes : le site et Léo ne proposent aucun créneau qui les chevauche, sans en donner la raison. Au CRM, planifier dessus demande une dérogation explicite."
        >
          <div className="site-admin__stack">
            <Toggle
              checked={draft.lunchBreak !== null && draft.lunchBreak !== undefined}
              onChange={(v) => set("lunchBreak", v ? { start: "12:00", end: "13:00" } : null)}
              label={draft.lunchBreak ? "Pause de midi active" : "Aucune pause de midi"}
              hint="Un rendez-vous du matin finit au plus tard au début de la pause ; l’après-midi commence au plus tôt à sa fin. Seul un soin long l’enjambe (voir Réservations automatiques)."
            />
            {draft.lunchBreak ? (
              <div className="form-grid">
                <Field label="Début de la pause">
                  <Input
                    type="time"
                    value={draft.lunchBreak.start}
                    onChange={(e) =>
                      set("lunchBreak", { ...draft.lunchBreak!, start: e.target.value })
                    }
                  />
                </Field>
                <Field label="Fin de la pause">
                  <Input
                    type="time"
                    value={draft.lunchBreak.end}
                    onChange={(e) =>
                      set("lunchBreak", { ...draft.lunchBreak!, end: e.target.value })
                    }
                  />
                </Field>
              </div>
            ) : null}
            <div className="site-admin__closures">
              <h3 className="site-admin__subtitle">Fermetures et plages bloquées</h3>
              {(draft.closures ?? []).length === 0 ? (
                <p className="site-admin__note">Aucune fermeture prévue.</p>
              ) : (
                <ul className="site-admin__closure-list">
                  {(draft.closures ?? []).map((c, i) => {
                    const patch = (changes: Partial<typeof c>) =>
                      set(
                        "closures",
                        (draft.closures ?? []).map((x, j) => (j === i ? { ...x, ...changes } : x)),
                      );
                    return (
                      <li key={c.id} className="site-admin__closure">
                        <Field label="Du">
                          <Input
                            type="date"
                            value={c.from}
                            onChange={(e) => patch({ from: e.target.value })}
                          />
                        </Field>
                        <Field label="Au" hint="Vide : sans fin (chaque semaine).">
                          <Input
                            type="date"
                            value={c.to ?? ""}
                            onChange={(e) => patch({ to: e.target.value || null })}
                          />
                        </Field>
                        <Field label="Jours" hint="Aucun : tous les jours de la période.">
                          <div className="site-admin__weekdays" role="group" aria-label="Jours">
                            {WEEKDAYS.map((d) => {
                              const on = (c.weekdays ?? []).includes(d);
                              return (
                                <button
                                  key={d}
                                  type="button"
                                  className={`chips__chip${on ? " is-active" : ""}`}
                                  aria-pressed={on}
                                  title={WEEKDAY_LABELS[d]}
                                  onClick={() => {
                                    const next = new Set(c.weekdays ?? []);
                                    if (next.has(d)) next.delete(d);
                                    else next.add(d);
                                    patch({ weekdays: next.size ? [...next].sort() : null });
                                  }}
                                >
                                  {WEEKDAY_LABELS[d].slice(0, 3)}
                                </button>
                              );
                            })}
                          </div>
                        </Field>
                        <Field label="De" hint="Vide : toute la journée.">
                          <Input
                            type="time"
                            value={c.startTime ?? ""}
                            onChange={(e) => patch({ startTime: e.target.value || null })}
                          />
                        </Field>
                        <Field label="À">
                          <Input
                            type="time"
                            value={c.endTime ?? ""}
                            onChange={(e) => patch({ endTime: e.target.value || null })}
                          />
                        </Field>
                        <Field label="Motif (interne)">
                          <Input
                            value={c.label}
                            maxLength={120}
                            placeholder="ex : Inventaire, congé férié"
                            onChange={(e) => patch({ label: e.target.value })}
                          />
                        </Field>
                        <Button
                          type="button"
                          variant="ghost-danger"
                          size="sm"
                          onClick={() =>
                            set(
                              "closures",
                              (draft.closures ?? []).filter((_, j) => j !== i),
                            )
                          }
                        >
                          Retirer
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="plus" size={14} />}
                  onClick={() => {
                    const today = new Date().toISOString().slice(0, 10);
                    set("closures", [
                      ...(draft.closures ?? []),
                      {
                        id: `f${Date.now().toString(36)}`,
                        from: today,
                        to: today,
                        weekdays: null,
                        label: "",
                      },
                    ]);
                  }}
                >
                  Ajouter une fermeture
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card
          title="Jours fériés"
          subtitle="Calendrier du Québec, prérempli chaque année : un férié coché est fermé (aucun créneau pour le site ni pour Léo ; au CRM, planifier ce jour-là demande une dérogation). Décochez les fériés travaillés."
        >
          <ul className="site-admin__holidays">
            {holidaysForYear(new Date().getFullYear(), draft.holidays ?? {}).map((h) => (
              <li key={h.key} className="site-admin__holiday">
                <Toggle
                  checked={h.closed}
                  onChange={(v) => set("holidays", { ...(draft.holidays ?? {}), [h.key]: v })}
                  label={h.label}
                  hint={`${holidayDate(h.date)} · ${h.closed ? "fermé" : "ouvert"}`}
                />
              </li>
            ))}
          </ul>
        </Card>

        <AutomaticBookingCard draft={draft} set={set} />

        <Card
          title="Acompte"
          subtitle="Demandé à la réservation, réglé par carte (Stripe) ; le solde se règle à l'atelier."
        >
          <div className="site-admin__stack">
            <div className="form-grid">
              <Field label="Acompte (% du total TTC)" hint="0 = pas d'acompte.">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={draft.depositPercent}
                  onChange={(e) => set("depositPercent", Number(e.target.value))}
                />
              </Field>
              <Field label="Acompte minimal ($)">
                <Input
                  type="number"
                  min={0}
                  step={5}
                  value={draft.depositMinCents / 100}
                  onChange={(e) => set("depositMinCents", Math.round(Number(e.target.value) * 100))}
                />
              </Field>
            </div>
            <Field label="Phrase affichée au client">
              <Textarea
                rows={2}
                value={draft.bookingNote}
                onChange={(e) => set("bookingNote", e.target.value)}
              />
            </Field>
            <dl className="kv">
              <dt>Stripe</dt>
              <dd>
                <Badge variant={data.stripeConfigured ? "success" : "warning"}>
                  {data.stripeConfigured ? "Configuré" : "Paiement à l’atelier"}
                </Badge>
              </dd>
            </dl>
            <p className="site-admin__note">
              {data.stripeConfigured
                ? "Les acomptes passent par Stripe Checkout et arrivent dans les paiements du rendez-vous."
                : "Le paiement en ligne n’est pas configuré. Aucun acompte n’est demandé : le client règle à l’atelier."}
            </p>
          </div>
        </Card>

        <Card title="Heures d'ouverture" subtitle="Affichées sur le site et bornes des créneaux.">
          <HoursEditor value={draft.hours} onChange={(hours) => set("hours", hours)} />
        </Card>

        <Card
          title="Coordonnées"
          subtitle="Affichées dans l'en-tête, le pied de page et la page Contact."
        >
          <div className="form-grid">
            <div className="form-grid__full">
              <Field label="Adresse">
                <Input
                  value={draft.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="ex : 2147, boulevard Talbot"
                />
              </Field>
            </div>
            <Field label="Ville">
              <Input value={draft.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field label="Code postal">
              <Input value={draft.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
            </Field>
            <Field label="Téléphone">
              <Input
                type="tel"
                value={draft.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </Field>
            <Field label="Courriel" hint="Reçoit les messages du formulaire de contact.">
              <Input
                type="email"
                value={draft.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <div className="form-grid__full">
              <Field label="Lien Google Maps">
                <Input
                  type="url"
                  value={draft.mapsUrl}
                  onChange={(e) => set("mapsUrl", e.target.value)}
                  placeholder="https://maps.google.com/…"
                />
              </Field>
            </div>
            <Field label="Instagram">
              <Input
                type="url"
                value={draft.instagramUrl}
                onChange={(e) => set("instagramUrl", e.target.value)}
              />
            </Field>
            <Field label="Facebook">
              <Input
                type="url"
                value={draft.facebookUrl}
                onChange={(e) => set("facebookUrl", e.target.value)}
              />
            </Field>
          </div>
        </Card>
      </div>

      <div className="site-admin__foot">{saveButton}</div>
    </div>
  );
}
